// Version management and cache busting utility

const APP_VERSION = '1.2.0'; // v1.2.0 - Accounts Module Addition (January 2025)
const VERSION_KEY = 'app_version';
const LAST_CHECK_KEY = 'last_version_check';
const CHECK_INTERVAL = 60 * 60 * 1000; // Check every 60 minutes (increased from 30)
const VERSION_CHECK_LOCK = 'version_check_in_progress';

/**
 * Check if app version has changed and clear cache if needed
 * Now with safeguards against multiple triggers and mobile PWA loops
 */
export const checkVersion = () => {
  // Prevent multiple checks happening simultaneously
  if (sessionStorage.getItem(VERSION_CHECK_LOCK)) {
    return false;
  }
  
  const storedVersion = localStorage.getItem(VERSION_KEY);
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  const now = Date.now();

  // Check if we should perform a version check - increased interval
  if (lastCheck && (now - parseInt(lastCheck)) < CHECK_INTERVAL) {
    return false; // Don't check too frequently
  }

  // Set lock to prevent concurrent checks
  sessionStorage.setItem(VERSION_CHECK_LOCK, 'true');

  // Update last check time FIRST to prevent rapid re-checks
  localStorage.setItem(LAST_CHECK_KEY, now.toString());

  // If version changed or first time
  if (!storedVersion || storedVersion !== APP_VERSION) {
    // Update version BEFORE clearing cache to prevent loops
    localStorage.setItem(VERSION_KEY, APP_VERSION);
    // Clear cache synchronously-safe way (don't await, don't block)
    clearAppCacheSafe();
    
    // Remove lock after a short delay
    setTimeout(() => {
      sessionStorage.removeItem(VERSION_CHECK_LOCK);
    }, 2000);
    
    return true; // Version changed
  }

  // Remove lock
  sessionStorage.removeItem(VERSION_CHECK_LOCK);
  return false;
};

/**
 * Safe cache clearing that won't cause reload loops
 * Only clears localStorage items, not service worker caches
 */
const clearAppCacheSafe = () => {
  try {
    // CRITICAL: These items must NEVER be cleared during version check
    const itemsToPreserve = [
      'token',           // JWT auth token
      'currentUser',     // User session data
      'user',            // Alternative user storage
      'impersonating',   // Master impersonation state
      VERSION_KEY,       // Prevent re-triggering version check
      LAST_CHECK_KEY     // Version check timestamp
    ];
    
    const allKeys = Object.keys(localStorage);
    
    allKeys.forEach(key => {
      if (!itemsToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Clear application cache (full version - use sparingly)
 * Critical: Preserves authentication data to prevent logout loops
 */
export const clearAppCache = async () => {
  try {
    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }

    // CRITICAL: These items must NEVER be cleared during version check
    // Clearing these causes logout/refresh loops
    const itemsToPreserve = [
      'token',           // JWT auth token
      'currentUser',     // User session data
      'user',            // Alternative user storage
      'impersonating',   // Master impersonation state
      VERSION_KEY,       // Prevent re-triggering version check
      LAST_CHECK_KEY     // Version check timestamp
    ];
    
    const allKeys = Object.keys(localStorage);
    
    allKeys.forEach(key => {
      if (!itemsToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    

    // Clear sessionStorage (preserve splash and version lock)
    const sessionKeysToPreserve = ['hasSeenSplash', VERSION_CHECK_LOCK];
    const sessionKeysAll = Object.keys(sessionStorage);
    sessionKeysAll.forEach(key => {
      if (!sessionKeysToPreserve.includes(key)) {
        sessionStorage.removeItem(key);
      }
    });

  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Force reload the page with cache bypass
 */
export const forceReload = () => {
  // Hard reload bypassing cache
  window.location.reload(true);
};

/**
 * Get current app version
 */
export const getAppVersion = () => APP_VERSION;

/**
 * Check if an update is available (compare stored version with current)
 * Returns { updateAvailable: boolean, currentVersion: string, storedVersion: string }
 */
export const checkForUpdates = async () => {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  return {
    updateAvailable: storedVersion && storedVersion !== APP_VERSION,
    currentVersion: APP_VERSION,
    storedVersion: storedVersion || APP_VERSION
  };
};

/**
 * Apply update - clears cache and reloads the app
 */
export const applyUpdate = async () => {
  await clearAppCache();
  localStorage.setItem(VERSION_KEY, APP_VERSION);
  forceReload();
};

/**
 * Show update notification to user (legacy - kept for backward compatibility)
 */
export const showUpdateNotification = (onUpdate) => {
  const userConfirmed = window.confirm(
    'A new version of MyVenueMate is available! Click OK to update and get the latest features.'
  );
  
  if (userConfirmed) {
    clearAppCache().then(() => {
      if (onUpdate) onUpdate();
      forceReload();
    });
  }
};

/**
 * Ensure fresh data load - Called when mounting data-heavy pages
 */
export const ensureFreshData = () => {
  // Clear sessionStorage to prevent stale data
  // (localStorage is preserved for auth)
  const sessionKeys = Object.keys(sessionStorage);
  sessionKeys.forEach(key => {
    if (!key.includes('auth') && !key.includes('token')) {
      sessionStorage.removeItem(key);
    }
  });
};

export default {
  checkVersion,
  clearAppCache,
  forceReload,
  getAppVersion,
  checkForUpdates,
  applyUpdate,
  showUpdateNotification,
  ensureFreshData
};
